import dayjs from 'dayjs';

import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { AGGREGATION_FIELDS } from '@/shared/api/openmetadata';
import { SEMANTIC_COLOR } from '@/shared/design-tokens/design-tokens';

import { TAG_DISTRIBUTION_CONFIG } from '@/features/tag-distribution/lib/constants';
import { isValidCategory, isValidTag } from '@/features/tag-distribution/lib/validators';
import { getTagsApi } from '@/features/tags/api';

import type { SearchApiResponse, TagDistributionApiResponse, TagDistributionItem } from './types';

/**
 * 태그 메타데이터(displayName, state)와 실제 자산 개수(aggregations)를 조합하여 태그 분포를 조회합니다
 * N+1 쿼리 문제를 해결하기 위해 모든 태그를 한 번에 가져와서 클라이언트에서 필터링합니다
 */
export const fetchTagDistribution = async (): Promise<TagDistributionApiResponse> => {
  try {
    const [searchResponse, tagsData] = await Promise.all([
      openMetadataApiClient.get<SearchApiResponse>('/search/query', {
        params: {
          q: '',
          index: 'dataAsset',
          from: 0,
          size: 0,
          deleted: false,
        },
      }),
      getTagsApi({
        limit: TAG_DISTRIBUTION_CONFIG.TAGS_LIMIT * 10,
        fields: 'usageCount,classification',
      }),
    ]);

    const rawTagAggregations = searchResponse.data.aggregations?.[AGGREGATION_FIELDS.TAGS]?.buckets || [];

    const docCountMap = new Map<string, number>();
    rawTagAggregations.forEach((item) => {
      const normalizedKey = item.key.toLowerCase();
      docCountMap.set(normalizedKey, item.doc_count);
    });

    const allTags: TagDistributionItem[] = [];

    (tagsData.data || []).forEach((tag) => {
      const categoryName = tag.classification?.name || tag.fullyQualifiedName.split('.')[0];

      if (!categoryName || !isValidCategory(categoryName) || !isValidTag(tag)) {
        return;
      }

      const normalizedKey = tag.fullyQualifiedName.toLowerCase();
      const docCount = docCountMap.get(normalizedKey);

      if (docCount && docCount > 0) {
        allTags.push({
          tag: tag.displayName || tag.name,
          tagFQN: tag.fullyQualifiedName,
          count: docCount,
          color: SEMANTIC_COLOR.CONTAINER.STANDARD.LEVEL1,
          percentage: 0,
        });
      }
    });

    const totalCount = allTags.reduce((sum, item) => sum + item.count, 0);
    allTags.forEach((item) => {
      item.percentage = totalCount > 0 ? Math.round((item.count / totalCount) * 100 * 10) / 10 : 0;
    });

    allTags.sort((a, b) => b.count - a.count);

    return {
      success: true,
      data: allTags,
      timestamp: dayjs().toISOString(),
    };
  } catch (error) {
    console.error('Failed to fetch tag distribution:', error);
    throw error;
  }
};
